// Hooks
import { useAppDispatch, useAppSelector } from '../app/hooks';

// Features
import { pushModalContent } from '../features/app/appSlice';
import { selectUser } from '../features/user/userSlice';
import {
    clearSession,
    fetchSessionAsync,
    selectSessionStatus,
} from '../features/session/sessionSlice';

// Models
import { LobbySession } from '../features/lobby/lobbyModels';
import { Session } from '../features/session/sessionModels';

// Constants
import {
    MODAL_PURCHASE_TOKEN,
    MODAL_INSUFFICIENT_TOKENS,
    MODAL_INSUFFICIENT_POINTS,
} from '../utils/constants/constants';

/**
 * @const useSessionAccess
 * Handles session access
 *
 * @param {Session|LobbySession} session A session
 * @return {function} A function
 */
function useSessionAccess(
    session: Session | LobbySession,
    targetSession: number,
    successText: string,
    inAnotherSession: boolean,
): {
    text: string;
    onClick: Function;
    disabled: boolean;
} {
    const dispatch = useAppDispatch();
    const user = useAppSelector(selectUser);
    const sessionStatus = useAppSelector(selectSessionStatus);
    const sessionNeedsToken =
        session !== null ? session.session.is_access_token : false;
    const userHasPoints =
        session !== null ? user.points >= session?.session.entryPrice : false;
    const userHasTokens = user.active_access_token_count > 0;
    const userCanBuyTokens = user.points >= 5000;

    function handleSessionEntryClick(session: number) {
        if (sessionStatus !== 'loading') {
            dispatch(clearSession());
            dispatch(fetchSessionAsync(session));
        }
    }

    return ((!sessionNeedsToken || (sessionNeedsToken && userHasTokens)) &&
        userHasPoints &&
        !inAnotherSession) ||
        (user.current_session !== null &&
            session.id === user.current_session.id)
        ? {
              text: sessionStatus !== 'loading' ? successText : 'Loading...',
              onClick: () => handleSessionEntryClick(targetSession),
              disabled: sessionStatus === 'loading',
          }
        : inAnotherSession === true
        ? {
              text: 'In another session',
              onClick: () => false,
              disabled: true,
          }
        : sessionNeedsToken && !userHasTokens && userCanBuyTokens
        ? {
              text: 'Get <b>Access Token</b>',
              onClick: () => dispatch(pushModalContent(MODAL_PURCHASE_TOKEN)),
              disabled: false,
          }
        : sessionNeedsToken && !userHasTokens && !userCanBuyTokens
        ? {
              text: 'Get <b>Access Token</b>',
              onClick: () =>
                  dispatch(pushModalContent(MODAL_INSUFFICIENT_TOKENS)),
              disabled: false,
          }
        : {
              text: successText,
              onClick: () =>
                  dispatch(pushModalContent(MODAL_INSUFFICIENT_POINTS)),
              disabled: false,
          };
}

// Exports
export default useSessionAccess;
